import React, { useState } from 'react';
import { serverTimestamp } from 'firebase/firestore';

interface LegalGateProps {
  onAccept: (legalData: any) => Promise<void>;
  currentLegalVersion: string;
}

export function LegalGate({ onAccept, currentLegalVersion }: LegalGateProps) {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [refundAccepted, setRefundAccepted] = useState(false);
  const [aiDisclaimerAccepted, setAiDisclaimerAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allAccepted = termsAccepted && privacyAccepted && refundAccepted && aiDisclaimerAccepted;

  const handleContinue = async () => {
    if (!allAccepted) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onAccept({
        legalAccepted: true,
        legalAcceptedAt: serverTimestamp(),
        legalAcceptedVersion: currentLegalVersion,
        termsAccepted: true,
        privacyAccepted: true,
        refundAccepted: true,
        aiDisclaimerAccepted: true
      });
    } catch (e) {
      console.error(e);
      setError("Could not save legal acceptance. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-4 min-h-screen bg-cv-page">
      <div className="bg-cv-panel border border-cv-border-strong rounded-2xl p-8 max-w-lg w-full shadow-2xl relative">
        <div className="mb-8 text-center">
          <h2 className="text-2xl font-black text-cv-primary uppercase tracking-tighter mb-2">Welcome to Chronovela</h2>
          <p className="text-xs text-cv-muted font-bold uppercase tracking-widest mb-4">Operated by Skald Haven Inc.</p>
          <p className="text-sm text-cv-muted leading-relaxed">
            Before you access the AI manuscript generation platform, you must review and agree to our terms and policies.
          </p>
        </div>

        <div className="space-y-4 mb-8 text-sm">
          <label className="flex items-start gap-3 cursor-pointer group">
            <input 
              type="checkbox" 
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-cv-border-strong bg-cv-elevated text-cv-interactive focus:ring-cv-interactive/40 focus:ring-offset-cv-page"
            />
            <span className="text-cv-secondary group-hover:text-cv-primary transition-colors">
              I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-cv-gold hover:text-cv-interactive underline" onClick={e => e.stopPropagation()}>Terms of Service</a>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer group">
            <input 
              type="checkbox" 
              checked={privacyAccepted}
              onChange={(e) => setPrivacyAccepted(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-cv-border-strong bg-cv-elevated text-cv-interactive focus:ring-cv-interactive/40 focus:ring-offset-cv-page"
            />
            <span className="text-cv-secondary group-hover:text-cv-primary transition-colors">
              I agree to the <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-cv-gold hover:text-cv-interactive underline" onClick={e => e.stopPropagation()}>Privacy Policy</a>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer group">
            <input 
              type="checkbox" 
              checked={refundAccepted}
              onChange={(e) => setRefundAccepted(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-cv-border-strong bg-cv-elevated text-cv-interactive focus:ring-cv-interactive/40 focus:ring-offset-cv-page"
            />
            <span className="text-cv-secondary group-hover:text-cv-primary transition-colors">
              I agree to the <a href="/refund-policy" target="_blank" rel="noopener noreferrer" className="text-cv-gold hover:text-cv-interactive underline" onClick={e => e.stopPropagation()}>Refund Policy</a>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer group">
            <input 
              type="checkbox" 
              checked={aiDisclaimerAccepted}
              onChange={(e) => setAiDisclaimerAccepted(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-cv-border-strong bg-cv-elevated text-cv-interactive focus:ring-cv-interactive/40 focus:ring-offset-cv-page"
            />
            <span className="text-cv-secondary group-hover:text-cv-primary transition-colors">
              I understand and agree to the <a href="/ai-disclaimer" target="_blank" rel="noopener noreferrer" className="text-cv-gold hover:text-cv-interactive underline" onClick={e => e.stopPropagation()}>AI Content Disclaimer</a>
            </span>
          </label>
        </div>

        {error && (
          <div className="mb-6 p-3 bg-red-950/40 border border-cv-danger/30 rounded text-cv-danger text-xs text-center font-bold">
            {error}
          </div>
        )}

        <button
          onClick={handleContinue}
          disabled={!allAccepted || isSubmitting}
          className={`w-full py-3 rounded-lg font-black uppercase tracking-widest text-sm transition-all flex items-center justify-center ${
            !allAccepted || isSubmitting
              ? 'bg-cv-elevated text-cv-muted cursor-not-allowed border border-cv-border-strong'
              : 'bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page shadow-[0_0_15px_rgba(113,153,184,0.35)] border border-cv-interactive'
          }`}
        >
          {isSubmitting ? 'Saving...' : 'Continue'}
        </button>
      </div>
    </div>
  );
}
